import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where, orderBy, limit } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useSubscriptions, usePackages } from '../../hooks/usePackages';
import { formatCurrency } from '../../utils/formatters';
import {
  ChartBarIcon,
  UserGroupIcon,
  BuildingOfficeIcon,
  CreditCardIcon,
  CurrencyDollarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';

const AnalyticsDashboard: React.FC = () => {
  const { subscriptions } = useSubscriptions();
  const { packages } = usePackages();
  const [loading, setLoading] = useState(true);
  const [orgCount, setOrgCount] = useState(0);
  const [activeOrgCount, setActiveOrgCount] = useState(0);
  const [userCount, setUserCount] = useState(0);
  const [activeUserCount, setActiveUserCount] = useState(0);
  const [recentOrgs, setRecentOrgs] = useState<{ id: string; name: string; createdAt: Date }[]>([]);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);

      const [orgsSnapshot, usersSnapshot, recentOrgsSnapshot] = await Promise.all([
        getDocs(collection(db, 'organizations')),
        getDocs(collection(db, 'users')),
        getDocs(query(collection(db, 'organizations'), orderBy('createdAt', 'desc'), limit(5)))
      ]);

      setOrgCount(orgsSnapshot.size);
      setActiveOrgCount(orgsSnapshot.docs.filter(d => d.data().isActive !== false).length);

      const users = usersSnapshot.docs.map(d => d.data());
      setUserCount(users.length);
      setActiveUserCount(users.filter(u => u.isActive !== false).length);

      setRecentOrgs(recentOrgsSnapshot.docs.map(d => ({
        id: d.id,
        name: d.data().name || 'Névtelen',
        createdAt: d.data().createdAt?.toDate() || new Date()
      })));
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPackageById = (id: string) => packages.find(p => p.id === id);

  const monthlyRevenue = subscriptions
    .filter(s => s.status === 'active')
    .reduce((total, sub) => {
      const pkg = getPackageById(sub.packageId);
      return total + (pkg?.price.monthly || 0);
    }, 0);

  const trialCount = subscriptions.filter(s => s.status === 'trial').length;
  const activeSubCount = subscriptions.filter(s => s.status === 'active').length;
  const suspendedCount = subscriptions.filter(s => s.status === 'suspended').length;

  const packageDistribution = packages.map(pkg => {
    const count = subscriptions.filter(s => s.packageId === pkg.id).length;
    return { name: pkg.name, count, color: pkg.type === 'free' ? 'gray' : pkg.type === 'basic' ? 'blue' : pkg.type === 'pro' ? 'purple' : 'green' };
  }).filter(p => p.count > 0);

  const maxDistCount = Math.max(...packageDistribution.map(p => p.count), 1);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Analitika</h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Üzleti statisztikák és növekedési mutatók</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={<BuildingOfficeIcon className="h-6 w-6" />}
          label="Szervezetek"
          value={orgCount}
          subtext={`${activeOrgCount} aktív`}
          color="purple"
        />
        <StatCard
          icon={<UserGroupIcon className="h-6 w-6" />}
          label="Felhasználók"
          value={userCount}
          subtext={`${activeUserCount} aktív`}
          color="green"
        />
        <StatCard
          icon={<CreditCardIcon className="h-6 w-6" />}
          label="Előfizetések"
          value={subscriptions.length}
          subtext={`${activeSubCount} aktív · ${trialCount} próbaidős`}
          color="indigo"
        />
        <StatCard
          icon={<CurrencyDollarIcon className="h-6 w-6" />}
          label="Havi bevétel"
          value={formatCurrency(monthlyRevenue, 'HUF')}
          subtext="Aktív előfizetésekből"
          color="emerald"
        />
      </div>

      {/* Subscription Status Breakdown */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Előfizetés státuszok</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatusCard label="Aktív" count={activeSubCount} icon={<CheckCircleIcon className="h-5 w-5" />} color="green" />
          <StatusCard label="Próbaidő" count={trialCount} icon={<ClockIcon className="h-5 w-5" />} color="blue" />
          <StatusCard label="Felfüggesztve" count={suspendedCount} icon={<ExclamationTriangleIcon className="h-5 w-5" />} color="yellow" />
          <StatusCard label="Összes" count={subscriptions.length} icon={<ChartBarIcon className="h-5 w-5" />} color="gray" />
        </div>
      </div>

      {/* Package Distribution */}
      {packageDistribution.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Csomag eloszlás</h3>
          <div className="space-y-3">
            {packageDistribution.map(pkg => (
              <div key={pkg.name}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{pkg.name}</span>
                  <span className="text-sm text-gray-500 dark:text-gray-400">{pkg.count} db</span>
                </div>
                <div className="w-full bg-gray-100 dark:bg-gray-700 rounded-full h-2.5">
                  <div
                    className={`h-2.5 rounded-full bg-${pkg.color}-500`}
                    style={{ width: `${(pkg.count / maxDistCount) * 100}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Organizations */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Legújabb szervezetek</h3>
        <div className="space-y-2">
          {recentOrgs.map(org => (
            <div key={org.id} className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-700 last:border-0">
              <div className="flex items-center space-x-3">
                <div className="p-1.5 bg-purple-100 dark:bg-purple-900/30 rounded-md">
                  <BuildingOfficeIcon className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                </div>
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{org.name}</span>
              </div>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {org.createdAt.toLocaleDateString('hu-HU')}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const colorClasses: Record<string, { bg: string; text: string }> = {
  purple: { bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-600 dark:text-purple-400' },
  green: { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-600 dark:text-green-400' },
  indigo: { bg: 'bg-indigo-100 dark:bg-indigo-900/30', text: 'text-indigo-600 dark:text-indigo-400' },
  emerald: { bg: 'bg-emerald-100 dark:bg-emerald-900/30', text: 'text-emerald-600 dark:text-emerald-400' },
  blue: { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-600 dark:text-blue-400' },
  yellow: { bg: 'bg-yellow-100 dark:bg-yellow-900/30', text: 'text-yellow-600 dark:text-yellow-400' },
  gray: { bg: 'bg-gray-100 dark:bg-gray-700', text: 'text-gray-600 dark:text-gray-400' },
};

const StatCard: React.FC<{ icon: React.ReactNode; label: string; value: string | number; subtext: string; color: string }> = ({ icon, label, value, subtext, color }) => {
  const c = colorClasses[color] || colorClasses.gray;
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5">
      <div className="flex items-center justify-between">
        <div className={`p-2 rounded-lg ${c.bg}`}>
          <span className={c.text}>{icon}</span>
        </div>
      </div>
      <div className="mt-3">
        <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{value}</p>
        <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{label}</p>
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{subtext}</p>
      </div>
    </div>
  );
};

const StatusCard: React.FC<{ label: string; count: number; icon: React.ReactNode; color: string }> = ({ label, count, icon, color }) => {
  const c = colorClasses[color] || colorClasses.gray;
  return (
    <div className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
      <span className={c.text}>{icon}</span>
      <div>
        <div className="text-lg font-bold text-gray-900 dark:text-gray-100">{count}</div>
        <div className="text-xs text-gray-500 dark:text-gray-400">{label}</div>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
